import { ChangeDetectorRef, Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGURenderService } from 'agu_app_shell';
import type { OEPView } from 'agu_oep_core';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { OEPContextService } from './oep-context.service';
import { OEPDataService } from '../data/oep-data.service';
import { ExplorerOEPHostAdapter } from '../host/oep-host.adapter';
import { OEPAt } from './oep-location';
import { OEPCoalescedRunner } from './oep-coalesced-runner';

/**
 * Every OEP view: zoneless repaint wiring (AGURenderService — MJExplorer has no zone), context
 * refresh on Changes$, navigation through the host adapter, and a Loading/Error/Busy trio.
 * Toasts go through MJNotificationService.
 */
@Directive()
export abstract class OEPViewBase implements OnInit, OnDestroy {
    protected readonly Cdr = inject(ChangeDetectorRef);
    protected readonly Render = inject(AGURenderService);
    protected readonly Context = inject(OEPContextService);
    protected readonly Data = inject(OEPDataService);
    protected readonly Host = inject(ExplorerOEPHostAdapter);
    private contextSub: Subscription | null = null;
    private readonly refresher = new OEPCoalescedRunner(() => this.runRefresh());
    private destroyed = false;

    Loading = true;
    Error: string | null = null;
    Busy = false;

    ngOnInit(): void {
        this.Render.Attach(this.Cdr);
        this.contextSub = this.Context.Changes$.subscribe(() => this.Changed());
        void this.Refresh();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.contextSub?.unsubscribe();
        this.Render.Detach(this.Cdr);
    }

    /**
     * No active season once loading settles. The surface shows its own between-seasons state before any
     * view renders; this covers a season that disappears under a mounted view (reload after a gate), so
     * a view never paints blank. Views render it with the surface's copy (oep-surface.component.html).
     */
    get NoSeason(): boolean {
        return !this.Loading && this.Error === null && this.Context.Season === null;
    }

    protected abstract Load(): Promise<void>;

    /**
     * (Re)loads the view. Never overlaps: a Refresh while one is in flight queues exactly one shared
     * follow-up that starts after it, so a refresh requested right after a write sees the write.
     */
    Refresh(): Promise<void> {
        return this.refresher.Again();
    }

    private async runRefresh(): Promise<void> {
        this.Loading = true;
        this.Error = null;
        this.Changed();
        try {
            await this.Load();
        } catch (err) {
            this.Error = err instanceof Error ? err.message : String(err);
        } finally {
            this.Loading = false;
            this.Changed();
        }
    }

    /**
     * Runs a write with Busy set; toasts success or the error message (and hands the message to
     * `onError` so a view can also show it in its own aria-live status); then reloads context so
     * counts and tasks update, and refreshes the view through Refresh() — never a bare Load(), so
     * the post-write load cannot overlap a refresh already in flight (it queues behind it).
     */
    protected async Act(work: () => Promise<void>, success: string | null, onError?: (message: string) => void): Promise<boolean> {
        if (this.Busy) return false;
        this.Busy = true;
        this.Changed();
        try {
            await work();
            if (success) MJNotificationService.Instance.CreateSimpleNotification(success, 'success', 2600);
            await this.Context.Reload();
            await this.Refresh();
            return true;
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            onError?.(message);
            MJNotificationService.Instance.CreateSimpleNotification(message, 'error', 5000);
            return false;
        } finally {
            this.Busy = false;
            this.Changed();
        }
    }

    /** Ask for a repaint (coalesced, after pending continuations). A destroyed view never asks. */
    protected Changed(): void {
        if (this.destroyed) return;
        this.Render.Notify();
    }

    Go(view: OEPView, id?: string): void {
        this.Host.NavigateTo(OEPAt(view, id));
    }
}
